import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Checkbox, message, Modal, Space, Table, Tag} from "antd";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import _ from "lodash";
import {AppContextContext, VulSourceServiceContext} from "../../Contexts";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import VulSource from "../../domain/VulSource";
import {useTableHandler} from "../../sal-ui/TableHandler";
import {ascendingFirstSortDirections, descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import PagedResult from "../../service/PagedResult";
import {formatDateTime} from "../../utils/FormatUtils";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import * as styles from "./VulSourceList.module.css";
import * as tackStyles from "../vulsourceitem/Tack.module.css";
import {formatVulSourceType} from "../../domain/VulSourceType";
import {ColumnsType} from "antd/es/table";
import MarkdownContent from "../common/MarkdownContent";
import {Permission} from "../../domain/auth/Permission";
import {formatVulSourceHealth, VulSourceHealth} from "../../domain/VulSourceHealth";
import {SingleSummaryBlock} from "../common/SummaryBlock";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {FormPersister} from "../../sal-ui/FormPersister";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery, loadPersistentOwnerFilterValues} from "../common/OwnerFilter";

const PERSISTENT_IDENT = "VulSourceList";
const FILTER_DEBOUNCE_MS = 500;

function VulSourceList() {
    const appContext = useContext(AppContextContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [sources, setSources] = useState<PagedResult<VulSource>>();
    const [filterValues, setFilterValues] = useState<any>();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    const title = "Vulnerability sources";
    const ownerFilterAccess = {
        canReadSystem: appContext.user.hasPermission(Permission.SOURCE__READ_SEAL),
        canReadOrganization: appContext.user.hasPermission(Permission.SOURCE__READ_ORG)
    };

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const values = loadPersistentOwnerFilterValues({
            appContext,
            access: ownerFilterAccess,
            filterFormPersister,
            location,
            navigate
        });

        setFilterValues(values);
    }, []);

    useEffect(() => {
        if (filterValues !== undefined) {
            debouncedOnFinishFilter(filterValues);
        }
    }, [filterValues]);

    const columns: ColumnsType<VulSource> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            className: styles["column-name"],
            render: renderName
        },
        {
            dataIndex: "sourceType",
            title: "Type",
            width: 200,
            align: "center",
            render: formatVulSourceType
        },
        {
            dataIndex: "itemCount",
            title: "Items",
            width: 100,
            align: "center",
            render: renderItemCount,
        },
        {
            dataIndex: ["organization", "name"],
            key: "organization",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            title: "Owner",
            align: "left",
            render: renderOrganization
        },
        {
            dataIndex: "enabled",
            title: "Enabled",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            width: 80,
            align: "center",
            render: value => <Checkbox checked={value} disabled={true}/>
        },
        {
            dataIndex: "lastRun",
            title: "Last collection",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            width: 160,
            align: "left",
            className: styles["column-date-time"],
            render: value => formatDateTime(value)
        },
        {
            dataIndex: "latestItemPublishedAt",
            title: "Latest published at",
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            width: 160,
            align: "left",
            className: styles["column-date-time"],
            render: value => formatDateTime(value)
        }
    ];

    if (appContext.user.hasPermission(Permission.SOURCE__MANAGE_ORG)) {
        columns.push(
            {
                title: "Actions",
                key: "actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        );
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <OwnerFilter
                    className={styles.filter}
                    values={filterValues}
                    defaultValues={createDefaultOwnerFilterValues(appContext, ownerFilterAccess)}
                    persistentStateIdent={PERSISTENT_IDENT}
                    canReadSystem={ownerFilterAccess.canReadSystem}
                    canReadOrganization={ownerFilterAccess.canReadOrganization}
                    onChange={setFilterValues}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    {appContext.user.hasPermission(Permission.SOURCE__MANAGE_ORG) &&
                        <Button type={"primary"}
                                icon={<PlusOutlined/>}
                                onClick={() => navigate("/vul-sources/add")}>Add</Button>
                    }

                    {appContext.user.isSystemAdmin() && <>
                        <Button onClick={() => {
                            Modal.confirm({
                                content: "Do you really want to collect vulnerability items from all enabled sources?",
                                okText: "Collect",
                                cancelText: "Cancel",
                                onOk: () => onCollectAllConfirm()
                            });
                        }}>
                            Collect all
                        </Button>

                        <Button onClick={() => {
                            Modal.confirm({
                                content: "Do you really want to update vulnerability items of all sources in OpenSearch?",
                                okText: "Update",
                                cancelText: "Cancel",
                                onOk: () => onUpdateAllSourcesInOpenSearch()
                            });
                        }}>
                            Update in OpenSearch
                        </Button>
                    </>}

                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       loading={tableHandler.loading}
                       dataSource={sources?.data}
                       size="middle"
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id"
                       columns={columns}
                />
            </>
        </DocumentTitle>
    )

    function renderOrganization(value: any, source: VulSource) {
        if (source.owner === ResourceOwnerType.SYSTEM) {
            return <i>System</i>;
        }

        return renderOrganizationLink(source.organization, value);
    }

    function renderItemCount(value: any, source: VulSource) {
        const filter: any = {
            typeHints: {},
            values: {
                sources: [source.id]
            }
        };

        const searchParams = new URLSearchParams();

        searchParams.set("filter", FormPersister.encodeUrlState(filter));

        return <a href={`vul-source-items?${searchParams.toString()}`} target={"_blank"}>{value}</a>;
    }

    function renderName(value: any, source: VulSource) {
        const tackStyle =
            (!source.enabled) ? 'tack-grey' : (source.health === VulSourceHealth.HEALTHY)
                ? 'tack-green' : (source.health === VulSourceHealth.LAST_RUN_OUT_OF_DATE)
                    ? 'tack-red' : (source.health === VulSourceHealth.LATEST_ITEM_PUBLISHED_AT_OUT_OF_DATE)
                        ? 'tack-orange' : 'tack-grey';

        return <>
            <div className={`${tackStyles.tack} ${tackStyles[tackStyle]}`}
                 title={(!source.enabled) ? 'health not available for disabled sources' : formatVulSourceHealth(source.health)}
            />

            <div><Link to={`/vul-sources/${source.id}`}>{value}</Link>{appContext.user.isSystemAdmin() && source.importedAt && <Tag style={{marginLeft: 8}} variant={"filled"} color={"blue"}>imported</Tag>}</div>

            {source.description && <SingleSummaryBlock><MarkdownContent children={source.description}/></SingleSummaryBlock>}

            {appContext.user.isSystemAdmin() && source.tags.length > 0 && <div style={{marginTop: 8}}>{source.tags?.map(value => <Tag>{value}</Tag>)}</div>}
        </>;
    }

    function renderAction(_value: unknown, vulSource: VulSource) {
        return (
            <Space>
                {(appContext.user.isSystemAdmin() || vulSource.organization?.id === appContext.user.organization?.id) &&
                    <Button icon={<ReloadOutlined/>} className={"ant-btn-icon-only"}
                            title={"Run"}
                            onClick={() => {
                                Modal.confirm({
                                    content: "Do you really want to run collection for this source?",
                                    okText: "Run",
                                    cancelText: "Cancel",
                                    onOk: () => onRunConfirm(vulSource)
                                });
                            }}/>
                }

                {(vulSource.owner !== ResourceOwnerType.SYSTEM || appContext.user.isSystemAdmin()) &&
                    <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                            title={"Delete"}
                            onClick={() => {
                                Modal.confirm({
                                    content: <>Do you really want to delete source <b>{vulSource.name}</b>?</>,
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    onOk: () => onDeleteConfirm(vulSource)
                                });
                            }}/>
                }
            </Space>
        )
    }

    function onDeleteConfirm(vulSource: VulSource) {
        vulSourceService.delete(vulSource)
            .then(() => {
                message.success(<>Source <b>{vulSource.name}</b> successfully deleted.</>);

                tableHandler.reload();
            });
    }

    function onRunConfirm(vulSource: VulSource) {
        vulSourceService.run(vulSource)
            .then(() => {
                message.success("Collection of vulnerability items started.");
            });
    }

    function onCollectAllConfirm() {
        vulSourceService.runAll()
            .then(() => {
                message.success("Collection from all enabled sources successfully queued.");
            });
    }

    function onUpdateAllSourcesInOpenSearch() {
        vulSourceService.updateAllSourcesInOpenSearch()
            .then(() => {
                message.success("Vulnerability items of all sources updated in OpenSearch successfully.");
            });
    }

    function onRow(vulSource: VulSource) {
        return {onDoubleClick: () => navigate(`/vul-sources/${vulSource.id}`)};
    }

    function onFinishFilter(values: any) {
        const rqbQuery = createOwnerRqbQuery(values);

        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(rqbQuery);
    }

    function reload() {
        return vulSourceService.getList(tableHandler.queryOptions, {includeItemCount: true}).then(value => {
            tableHandler.updateTotal(value.total);

            setSources(value);
        });
    }
}

export default VulSourceList;
